/**
 * UK International Territorial Levels (ITL) – January 2025
 * Source: Office for National Statistics (ONS)
 */

export type ITL1Code =
  | "TLC" // North East (England)
  | "TLD" // North West (England)
  | "TLE" // Yorkshire and The Humber
  | "TLF" // East Midlands (England)
  | "TLG" // West Midlands (England)
  | "TLH" // East (England)
  | "TLI" // London
  | "TLJ" // South East (England)
  | "TLK" // South West (England)
  | "TLL" // Wales
  | "TLM" // Scotland
  | "TLN"; // Northern Ireland

export type ITL2Code =
  // North East
  | "TLC3" | "TLC4"
  // North West
  | "TLD1" | "TLD3" | "TLD4" | "TLD6" | "TLD7"
  // Yorkshire and The Humber
  | "TLE1" | "TLE2" | "TLE3" | "TLE4"
  // East Midlands
  | "TLF1" | "TLF2" | "TLF3"
  // West Midlands
  | "TLG1" | "TLG2" | "TLG3"
  // East
  | "TLH2" | "TLH3" | "TLH4" | "TLH5" | "TLH6"
  // London
  | "TLI3" | "TLI4" | "TLI5" | "TLI6" | "TLI7"
  // South East
  | "TLJ1" | "TLJ2" | "TLJ3" | "TLJ4"
  // South West
  | "TLK3" | "TLK4" | "TLK5" | "TLK6" | "TLK7"
  // Wales
  | "TLL3" | "TLL4" | "TLL5"
  // Scotland
  | "TLM0" | "TLM1" | "TLM2" | "TLM3" | "TLM5" | "TLM9"
  // Northern Ireland
  | "TLN0";

export interface ITLRegion {
  code: string;
  name: string;
}

/** ITL 1 regions (12) */
export const ITL1_REGIONS: readonly ITLRegion[] = [
  { code: "TLC", name: "North East (England)" },
  { code: "TLD", name: "North West (England)" },
  { code: "TLE", name: "Yorkshire and The Humber" },
  { code: "TLF", name: "East Midlands (England)" },
  { code: "TLG", name: "West Midlands (England)" },
  { code: "TLH", name: "East (England)" },
  { code: "TLI", name: "London" },
  { code: "TLJ", name: "South East (England)" },
  { code: "TLK", name: "South West (England)" },
  { code: "TLL", name: "Wales" },
  { code: "TLM", name: "Scotland" },
  { code: "TLN", name: "Northern Ireland" },
] as const;

/** ITL 2 regions (46) */
export const ITL2_REGIONS: readonly ITLRegion[] = [
  // North East
  { code: "TLC3", name: "Tees Valley" },
  { code: "TLC4", name: "Northumberland, Durham and Tyne & Wear" },

  // North West
  { code: "TLD1", name: "Cumbria" },
  { code: "TLD3", name: "Greater Manchester" },
  { code: "TLD4", name: "Lancashire" },
  { code: "TLD6", name: "Cheshire" },
  { code: "TLD7", name: "Merseyside" },

  // Yorkshire and The Humber
  { code: "TLE1", name: "East Yorkshire and Northern Lincolnshire" },
  { code: "TLE2", name: "North Yorkshire" },
  { code: "TLE3", name: "South Yorkshire" },
  { code: "TLE4", name: "West Yorkshire" },

  // East Midlands
  { code: "TLF1", name: "Derbyshire and Nottinghamshire" },
  { code: "TLF2", name: "Leicestershire, Rutland and Northamptonshire" },
  { code: "TLF3", name: "Lincolnshire" },

  // West Midlands
  { code: "TLG1", name: "Herefordshire, Worcestershire and Warwickshire" },
  { code: "TLG2", name: "Shropshire and Staffordshire" },
  { code: "TLG3", name: "West Midlands" },

  // East
  { code: "TLH2", name: "Bedfordshire and Hertfordshire" },
  { code: "TLH3", name: "Essex" },
  { code: "TLH4", name: "Cambridgeshire and Peterborough" },
  { code: "TLH5", name: "Norfolk" },
  { code: "TLH6", name: "Suffolk" },

  // London
  { code: "TLI3", name: "Inner London - West" },
  { code: "TLI4", name: "Inner London - East" },
  { code: "TLI5", name: "Outer London - East and North East" },
  { code: "TLI6", name: "Outer London - South" },
  { code: "TLI7", name: "Outer London - West and North West" },

  // South East
  { code: "TLJ1", name: "Berkshire, Buckinghamshire and Oxfordshire" },
  { code: "TLJ2", name: "Surrey, East and West Sussex" },
  { code: "TLJ3", name: "Hampshire and Isle of Wight" },
  { code: "TLJ4", name: "Kent" },

  // South West
  { code: "TLK3", name: "Cornwall and Isles of Scilly" },
  { code: "TLK4", name: "Devon" },
  { code: "TLK5", name: "West of England" },
  { code: "TLK6", name: "North Somerset, Somerset and Dorset" },
  { code: "TLK7", name: "Gloucestershire and Wiltshire" },

  // Wales
  { code: "TLL3", name: "North Wales" },
  { code: "TLL4", name: "Mid and South West Wales" },
  { code: "TLL5", name: "South East Wales" },

  // Scotland
  { code: "TLM0", name: "Eastern Scotland" },
  { code: "TLM1", name: "East Central Scotland" },
  { code: "TLM2", name: "Highlands and Islands" },
  { code: "TLM3", name: "West Central Scotland" },
  { code: "TLM5", name: "North Eastern Scotland" },
  { code: "TLM9", name: "Southern Scotland" },

  // Northern Ireland
  { code: "TLN0", name: "Northern Ireland" },
] as const;

/** Lookup helpers */
export const ITL1_BY_CODE = Object.fromEntries(
  ITL1_REGIONS.map((r) => [r.code, r])
) as Record<ITL1Code, ITLRegion>;

export const ITL2_BY_CODE = Object.fromEntries(
  ITL2_REGIONS.map((r) => [r.code, r])
) as Record<ITL2Code, ITLRegion>;
